/*
 * Area "billing"
 * Owned by one wave-2 agent; see TRANSLATING.md, "Wave 2 partition".
 *
 * One entry per key: key: { en: '...', fr: '...', es: '...' }.
 * New keys are billing_<screen>_<name>, or the Android key from the translation memory
 * when the English and the meaning are the same (then copy its fr/es verbatim).
 * Look in common.js first. en is required; a missing fr/es falls back to English.
 */
export default {
  // ---- Shared within the area
  billing_last_updated: { en: 'Last updated: {0}', fr: 'Dernière mise à jour : {0}', es: 'Última actualización: {0}' },
  billing_pay: { en: 'Pay', fr: 'Payer', es: 'Pagar' },
  billing_etransfer: { en: 'E-Transfer', fr: 'Virement Interac', es: 'Transferencia electrónica' },
  billing_sign_in_to_account: { en: 'Sign In to Account', fr: 'Se connecter au compte', es: 'Iniciar sesión en la cuenta' },
  billing_return_home: { en: 'Return to Homepage', fr: "Retour à la page d'accueil", es: 'Volver a la página de inicio' },
  billing_beneficiary_name: { en: 'Beneficiary Name: {0}', fr: 'Nom du bénéficiaire : {0}', es: 'Nombre del beneficiario: {0}' },
  billing_bank_no: { en: 'Bank No: {0}', fr: 'N° de banque : {0}', es: 'N.º de banco: {0}' },
  billing_transit_no: { en: 'Transit No: {0}', fr: 'N° de transit : {0}', es: 'N.º de tránsito: {0}' },
  billing_account_no: { en: 'Account No: {0}', fr: 'N° de compte : {0}', es: 'N.º de cuenta: {0}' },
  billing_swift_code: { en: 'SWIFT Code: {0}', fr: 'Code SWIFT : {0}', es: 'Código SWIFT: {0}' },
  billing_agreement_title: {
    en: 'ReadyChek SaaS Agreement (Terms of Service)',
    fr: "Contrat SaaS de ReadyChek (conditions d'utilisation)",
    es: 'Acuerdo SaaS de ReadyChek (Términos del servicio)',
  },
  billing_not_set: { en: 'Not Set', fr: 'Non défini', es: 'Sin definir' },
  billing_unknown_error: { en: 'Unknown error', fr: 'Erreur inconnue', es: 'Error desconocido' },
  billing_unexpected_error: { en: 'An unexpected error occurred.', fr: "Une erreur inattendue s'est produite.", es: 'Se produjo un error inesperado.' },
  billing_save_failed: { en: 'Failed to save: {0}', fr: "Échec de l'enregistrement : {0}", es: 'No se pudo guardar: {0}' },
  billing_added: { en: 'Added', fr: 'Ajouté', es: 'Añadido' },
  billing_select: { en: 'Select', fr: 'Sélectionner', es: 'Seleccionar' },
  dailylog_not_available: { en: 'N/A', fr: 'S. O.', es: 'N/D' },
  labelDetailsShow: { en: 'Show Details', fr: 'Afficher les détails', es: 'Mostrar detalles' },
  labelMinors: { en: 'Minors', fr: 'Mineures', es: 'Menores' },
  labelMajors: { en: 'Majors', fr: 'Majeures', es: 'Mayores' },
  labelInspection: { en: 'Inspection', fr: 'Inspection', es: 'Inspección' },
  labelInspectionDetails: { en: 'Inspection Details', fr: "Détails de l'inspection", es: 'Detalles de la inspección' },
  app_messages_title: { en: 'Messages', fr: 'Messages', es: 'Mensajes' },

  // ---- Billing page
  billing_title: { en: 'Billing', fr: 'Facturation', es: 'Facturación' },
  billing_page_title: { en: 'Billing - ReadyChek', fr: 'Facturation - ReadyChek', es: 'Facturación - ReadyChek' },
  billing_loading: { en: 'Loading Billing Details...', fr: 'Chargement des détails de facturation...', es: 'Cargando los datos de facturación...' },
  billing_payment_methods_title: { en: 'Payment Methods', fr: 'Modes de paiement', es: 'Métodos de pago' },
  billing_payment_methods_subtitle: {
    en: 'This information will be used to pay your invoice between the 15th and the last day of the month',
    fr: 'Ces renseignements serviront à payer votre facture entre le 15 et le dernier jour du mois',
    es: 'Esta información se utilizará para pagar su factura entre el día 15 y el último día del mes',
  },
  billing_card_required_alert: {
    en: 'Action Required: You must add a Credit Card before your contract start date on {0}.',
    fr: 'Action requise : vous devez ajouter une carte de crédit avant la date de début de votre contrat, le {0}.',
    es: 'Acción requerida: debe añadir una tarjeta de crédito antes de la fecha de inicio de su contrato, el {0}.',
  },
  billing_plan_summary: { en: '{0} using {1} starting on {2}', fr: '{0} par {1} à compter du {2}', es: '{0} con {1} a partir del {2}' },
  billing_autopay_info: {
    en: 'Set up Auto-Pay to have your card charged automatically.',
    fr: 'Configurez le paiement automatique pour que votre carte soit débitée automatiquement.',
    es: 'Configure el pago automático para que se cargue su tarjeta automáticamente.',
  },
  billing_copy_card_name: { en: 'Copy card holder name', fr: 'Copier le nom du titulaire de la carte', es: 'Copiar el nombre del titular de la tarjeta' },
  billing_card_name_copied: {
    en: 'Card holder name copied to clipboard.',
    fr: 'Nom du titulaire de la carte copié dans le presse-papiers.',
    es: 'Nombre del titular de la tarjeta copiado al portapapeles.',
  },
  billing_card_name_copy_failed: {
    en: 'Could not copy the card holder name.',
    fr: 'Impossible de copier le nom du titulaire de la carte.',
    es: 'No se pudo copiar el nombre del titular de la tarjeta.',
  },
  billing_card_number_value: { en: 'Card Number: {0}', fr: 'Numéro de carte : {0}', es: 'Número de tarjeta: {0}' },
  billing_expiry_label: { en: 'Expiry:', fr: 'Expiration :', es: 'Vencimiento:' },
  billing_expired: { en: 'Expired', fr: 'Expirée', es: 'Vencida' },
  billing_expires_this_month: { en: 'Expires this month', fr: 'Expire ce mois-ci', es: 'Vence este mes' },
  billing_no_amex: { en: 'We do not accept American Express.', fr: "Nous n'acceptons pas American Express.", es: 'No aceptamos American Express.' },
  billing_add_card_anytime: {
    en: 'Make payments a breeze, add your credit card at anytime.',
    fr: 'Simplifiez vos paiements : ajoutez votre carte de crédit en tout temps.',
    es: 'Facilite sus pagos: añada su tarjeta de crédito en cualquier momento.',
  },
  billing_no_amex_note: {
    en: 'NOTE: We do not accept American Express.',
    fr: "REMARQUE : Nous n'acceptons pas American Express.",
    es: 'NOTA: No aceptamos American Express.',
  },
  billing_update_card: { en: 'Update Card', fr: 'Modifier la carte', es: 'Actualizar tarjeta' },
  billing_add_card: { en: 'Add Card', fr: 'Ajouter une carte', es: 'Añadir tarjeta' },
  billing_manual_payment_options: { en: 'MANUAL PAYMENT OPTIONS', fr: 'OPTIONS DE PAIEMENT MANUEL', es: 'OPCIONES DE PAGO MANUAL' },
  billing_account_title: { en: 'Billing Account', fr: 'Compte de facturation', es: 'Cuenta de facturación' },
  billing_account_subtitle: {
    en: 'Your account standing and invoice details',
    fr: 'État de votre compte et détails de facturation',
    es: 'Estado de su cuenta y datos de facturación',
  },
  billing_invoice_details_required: {
    en: 'Action Required: Please complete your invoice details (Company Name, Contact Name, Email).',
    fr: "Action requise : veuillez remplir les détails de facturation (nom de l'entreprise, nom de la personne-ressource, courriel).",
    es: 'Acción requerida: complete los datos de facturación (nombre de la empresa, nombre del contacto, correo electrónico).',
  },
  billing_company_header: { en: 'BILLING COMPANY', fr: 'ENTREPRISE FACTURÉE', es: 'EMPRESA DE FACTURACIÓN' },
  billing_contact_header: { en: 'BILLING CONTACT', fr: 'CONTACT DE FACTURATION', es: 'CONTACTO DE FACTURACIÓN' },
  billing_notifications_header: { en: 'NOTIFICATIONS', fr: 'NOTIFICATIONS', es: 'NOTIFICACIONES' },
  billing_invoice_emails_sent_to: { en: 'Invoice Emails Sent To', fr: 'Factures envoyées par courriel à', es: 'Facturas enviadas por correo a' },
  billing_purchase_order: { en: 'Purchase Order #', fr: 'N° de bon de commande', es: 'N.º de orden de compra' },
  billing_not_set_bracketed: { en: '[Not Set]', fr: '[Non défini]', es: '[Sin definir]' },
  billing_update_details: { en: 'Update Details', fr: 'Modifier les détails', es: 'Actualizar datos' },
  billing_credits_header: { en: 'CREDITS AND OVERPAYMENTS', fr: 'CRÉDITS ET TROP-PAYÉS', es: 'CRÉDITOS Y PAGOS EN EXCESO' },
  billing_overpayment_balance: { en: 'Overpayment Balance', fr: 'Solde de trop-payé', es: 'Saldo de pago en exceso' },
  billing_credits: { en: 'Credits', fr: 'Crédits', es: 'Créditos' },
  billing_credits_hint: {
    en: 'These are amounts in your favour, not what you owe. Any amount owing is shown on your',
    fr: "Ces montants sont en votre faveur; il ne s'agit pas de ce que vous devez. Tout montant dû figure sur votre",
    es: 'Estos importes son a su favor, no lo que debe. Cualquier importe adeudado se muestra en su',
  },
  billing_invoices_page_link: { en: 'Invoices page', fr: 'page Factures', es: 'página de Facturas' },
  billing_plan_title: { en: 'Plan Information', fr: 'Renseignements sur le forfait', es: 'Información del plan' },
  billing_plan_subtitle: {
    en: 'Your current subscription details which start on {0}',
    fr: 'Les détails de votre abonnement actuel, qui commence le {0}',
    es: 'Los detalles de su suscripción actual, que comienza el {0}',
  },
  billing_subject_to: { en: 'Subject to the', fr: 'Assujetti au', es: 'Sujeto al' },
  billing_agreement_load_error: {
    en: 'Could not load the ReadyChek SaaS Agreement.',
    fr: 'Impossible de charger le contrat SaaS de ReadyChek.',
    es: 'No se pudo cargar el Acuerdo SaaS de ReadyChek.',
  },

  // ---- Plan lines (useAsset)
  billing_plan_vehicle_storage: { en: 'Vehicle Storage Fee', fr: 'Frais de stockage des véhicules', es: 'Tarifa de almacenamiento de vehículos' },
  billing_plan_vehicle_inspections: { en: 'Vehicle Inspections', fr: 'Inspections de véhicules', es: 'Inspecciones de vehículos' },
  billing_plan_equipment_storage: { en: 'Equipment Storage Fee', fr: "Frais de stockage de l'équipement", es: 'Tarifa de almacenamiento de equipos' },
  billing_plan_equipment_inspections: { en: 'Equipment Inspections', fr: "Inspections d'équipement", es: 'Inspecciones de equipos' },
  billing_plan_gps_device: { en: 'GPS Device', fr: 'Appareil GPS', es: 'Dispositivo GPS' },
  billing_plan_phone_support: { en: 'Monthly Phone Support', fr: 'Soutien téléphonique mensuel', es: 'Soporte telefónico mensual' },
  billing_plan_flat_rate: { en: 'Flat Rate', fr: 'Forfait fixe', es: 'Tarifa plana' },
  billing_plan_sso: { en: 'Single Sign On', fr: 'Authentification unique', es: 'Inicio de sesión único' },
  billing_plan_track_sleeper: { en: 'Track Sleeper', fr: 'Suivi de la couchette', es: 'Seguimiento de litera' },
  billing_price_per_vehicle_month: { en: '{0} / vehicle / month', fr: '{0} / véhicule / mois', es: '{0} / vehículo / mes' },
  billing_price_per_inspection: { en: '{0} / inspection', fr: '{0} / inspection', es: '{0} / inspección' },
  billing_price_per_active_day: { en: '{0} / active day', fr: '{0} / jour actif', es: '{0} / día activo' },
  billing_price_per_equipment_month: { en: '{0} / equipment / month', fr: '{0} / équipement / mois', es: '{0} / equipo / mes' },
  billing_price_per_device_month: { en: '{0} / device / month', fr: '{0} / appareil / mois', es: '{0} / dispositivo / mes' },
  billing_price_per_month: { en: '{0} / month', fr: '{0} / mois', es: '{0} / mes' },

  // ---- Card dialog
  billing_card_dialog_title: { en: 'Update Payment Details', fr: 'Modifier les détails de paiement', es: 'Actualizar datos de pago' },
  billing_card_details_header: { en: 'CARD DETAILS', fr: 'DÉTAILS DE LA CARTE', es: 'DATOS DE LA TARJETA' },
  billing_card_name: { en: 'Name on Card', fr: 'Nom sur la carte', es: 'Nombre en la tarjeta' },
  billing_card_number: { en: 'Card Number', fr: 'Numéro de carte', es: 'Número de tarjeta' },
  billing_card_expiry_month: { en: 'Expiry Month', fr: "Mois d'expiration", es: 'Mes de vencimiento' },
  billing_card_expiry_year: { en: 'Expiry Year', fr: "Année d'expiration", es: 'Año de vencimiento' },
  billing_card_cvc: { en: 'CVC', fr: 'CVC', es: 'CVC' },
  billing_cardholder_address_header: { en: "CARDHOLDER'S ADDRESS", fr: 'ADRESSE DU TITULAIRE DE LA CARTE', es: 'DIRECCIÓN DEL TITULAR DE LA TARJETA' },
  billing_fill_required: { en: 'Please fill in all required fields.', fr: 'Veuillez remplir tous les champs obligatoires.', es: 'Complete todos los campos obligatorios.' },
  billing_card_update_failed: {
    en: 'Failed to update card details.',
    fr: 'Échec de la mise à jour des détails de la carte.',
    es: 'No se pudieron actualizar los datos de la tarjeta.',
  },

  // ---- Billing details dialog
  billing_details_dialog_title: {
    en: 'Update Billing and Invoice Details',
    fr: 'Modifier les détails de facturation',
    es: 'Actualizar datos de facturación',
  },
  billing_company_name: { en: 'Billing Company Name', fr: "Nom de l'entreprise facturée", es: 'Nombre de la empresa de facturación' },
  billing_contact_name: { en: 'Contact First and Last Name', fr: 'Prénom et nom de la personne-ressource', es: 'Nombre y apellido del contacto' },
  billing_invoice_emails: { en: 'Invoice Emails', fr: 'Courriels de facturation', es: 'Correos de facturas' },
  billing_invoice_emails_description: {
    en: 'A copy of the invoice will be sent to these addresses.',
    fr: 'Une copie de la facture sera envoyée à ces adresses.',
    es: 'Se enviará una copia de la factura a estas direcciones.',
  },
  billing_purchase_order_hint: {
    en: 'This PO number will appear on the invoice',
    fr: 'Ce numéro de bon de commande figurera sur la facture',
    es: 'Este número de orden de compra aparecerá en la factura',
  },
  billing_fill_contact_fields: {
    en: 'Please fill in all required contact fields.',
    fr: 'Veuillez remplir tous les champs obligatoires de la personne-ressource.',
    es: 'Complete todos los campos obligatorios del contacto.',
  },
  billing_contact_email_invalid: {
    en: 'Contact Email is not a valid email address.',
    fr: "Le courriel de la personne-ressource n'est pas une adresse valide.",
    es: 'El correo electrónico del contacto no es una dirección válida.',
  },
  billing_enter_valid_email: {
    en: 'Please enter a valid email address.',
    fr: 'Veuillez saisir une adresse courriel valide.',
    es: 'Introduzca una dirección de correo electrónico válida.',
  },
  billing_email_already_listed: {
    en: 'This email is already in the list.',
    fr: 'Ce courriel est déjà dans la liste.',
    es: 'Este correo electrónico ya está en la lista.',
  },
  billing_save_details_failed: { en: 'Failed to save details.', fr: "Échec de l'enregistrement des détails.", es: 'No se pudieron guardar los datos.' },

  // ---- Paid / payment link pages (public)
  billing_paid_update_error: { en: 'Update Error', fr: 'Erreur de mise à jour', es: 'Error de actualización' },
  billing_paid_thank_you: { en: 'Thank-you!', fr: 'Merci !', es: '¡Gracias!' },
  billing_paid_thanks_for_using: { en: 'Thanks for using ReadyChek.', fr: "Merci d'utiliser ReadyChek.", es: 'Gracias por usar ReadyChek.' },
  billing_paid_charged: {
    en: "We've successfully charged your credit card.",
    fr: 'Nous avons débité votre carte de crédit avec succès.',
    es: 'Hemos cargado su tarjeta de crédito correctamente.',
  },
  billing_paid_statement_before: { en: 'The charge will appear as', fr: 'Le débit apparaîtra sous le nom', es: 'El cargo aparecerá como' },
  billing_paid_statement_after: { en: 'on your statement.', fr: 'sur votre relevé.', es: 'en su estado de cuenta.' },
  billing_paid_loading: { en: 'Loading details for {0}...', fr: 'Chargement des détails de {0}...', es: 'Cargando los detalles de {0}...' },
  billing_invoice_number: { en: 'Invoice # {0}', fr: 'Facture n° {0}', es: 'Factura n.º {0}' },
  billing_payment_error: { en: 'Payment Error', fr: 'Erreur de paiement', es: 'Error de pago' },
  billing_contact_support: { en: 'Contact Support', fr: 'Contacter le soutien', es: 'Contactar con soporte' },
  billing_generating_link: { en: 'Generating Payment Link', fr: 'Création du lien de paiement', es: 'Generando el enlace de pago' },
  billing_generating_link_details: {
    en: 'Securely preparing your checkout experience. You will be redirected automatically.',
    fr: 'Préparation sécurisée de votre paiement. Vous serez redirigé automatiquement.',
    es: 'Preparando su pago de forma segura. Será redirigido automáticamente.',
  },
  billing_working: { en: 'Working, please wait...', fr: 'Traitement en cours, veuillez patienter...', es: 'Procesando, espere...' },
  billing_proceed_now: { en: 'Click to proceed now', fr: 'Cliquez pour continuer maintenant', es: 'Haga clic para continuar ahora' },
  billing_thank_you: { en: 'Thank you!', fr: 'Merci !', es: '¡Gracias!' },
  billing_already_paid: {
    en: 'This invoice has already been paid. We appreciate your business!',
    fr: 'Cette facture a déjà été payée. Merci de votre confiance !',
    es: 'Esta factura ya ha sido pagada. ¡Agradecemos su preferencia!',
  },

  // ---- Invoices page and table
  billing_invoices_page_title: { en: 'Invoices - ReadyChek', fr: 'Factures - ReadyChek', es: 'Facturas - ReadyChek' },
  billing_invoice_search: { en: 'Search Invoices', fr: 'Rechercher des factures', es: 'Buscar facturas' },
  billing_download_print: { en: 'Download/Print', fr: 'Télécharger/Imprimer', es: 'Descargar/Imprimir' },
  billing_col_invoice_number: { en: 'Invoice #', fr: 'N° de facture', es: 'N.º de factura' },
  billing_col_issued_on: { en: 'Issued On', fr: 'Émise le', es: 'Emitida el' },
  billing_col_pre_tax_total: { en: 'Pre Tax Total', fr: 'Total avant taxes', es: 'Total antes de impuestos' },
  billing_col_payments: { en: 'Payments', fr: 'Paiements', es: 'Pagos' },
  billing_col_remaining: { en: 'Remaining', fr: 'Restant', es: 'Pendiente' },
  billing_col_due_date: { en: 'Due Date', fr: "Date d'échéance", es: 'Fecha de vencimiento' },
  billing_col_paid: { en: 'Paid', fr: 'Payée', es: 'Pagada' },
  billing_due_this_week: { en: 'Due this week', fr: 'Échéance cette semaine', es: 'Vence esta semana' },
  billing_past_due: { en: 'Past Due', fr: 'En retard', es: 'Vencida' },

  // ---- Invoice details dialog
  billing_invoice_details_title: { en: 'Invoice Details', fr: 'Détails de la facture', es: 'Detalles de la factura' },
  billing_paid_stamp: { en: '!! PAID !!', fr: '!! PAYÉE !!', es: '!! PAGADA !!' },
  billing_paid_on: { en: 'On {0}', fr: 'Le {0}', es: 'El {0}' },
  billing_invoice_heading: { en: 'INVOICE', fr: 'FACTURE', es: 'FACTURA' },
  billing_from: { en: 'FROM:', fr: 'DE :', es: 'DE:' },
  billing_invoice_to: { en: 'INVOICE TO:', fr: 'FACTURÉ À :', es: 'FACTURAR A:' },
  billing_invoice_date: { en: 'INVOICE DATE', fr: 'DATE DE FACTURE', es: 'FECHA DE FACTURA' },
  billing_payment_terms: { en: 'PAYMENT TERMS', fr: 'CONDITIONS DE PAIEMENT', es: 'CONDICIONES DE PAGO' },
  billing_net_30: { en: 'Net 30 Days', fr: 'Net 30 jours', es: 'Neto 30 días' },
  billing_due_date_caps: { en: 'DUE DATE', fr: "DATE D'ÉCHÉANCE", es: 'FECHA DE VENCIMIENTO' },
  billing_paid_date_caps: { en: 'PAID DATE', fr: 'DATE DE PAIEMENT', es: 'FECHA DE PAGO' },
  billing_line_details: { en: 'DETAILS', fr: 'DÉTAILS', es: 'DETALLES' },
  billing_line_qty: { en: 'QTY', fr: 'QTÉ', es: 'CANT.' },
  billing_line_rate: { en: 'RATE', fr: 'TARIF', es: 'TARIFA' },
  billing_line_amount: { en: 'AMOUNT', fr: 'MONTANT', es: 'IMPORTE' },
  billing_discount: { en: 'Discount', fr: 'Remise', es: 'Descuento' },
  billing_subtotal: { en: 'Subtotal', fr: 'Sous-total', es: 'Subtotal' },
  billing_tax: { en: 'Tax', fr: 'Taxes', es: 'Impuestos' },
  billing_total_plus_tax: { en: 'Total + Tax', fr: 'Total + taxes', es: 'Total + impuestos' },
  billing_payment_from: { en: 'Payment from {0} on {1}', fr: 'Paiement par {0} le {1}', es: 'Pago mediante {0} el {1}' },
  billing_payment_credit: { en: 'Credit', fr: 'Crédit', es: 'Crédito' },
  billing_payment_over_payment: { en: 'Over Payment', fr: 'Trop-payé', es: 'Pago en exceso' },
  billing_total_due: { en: 'Total Due', fr: 'Total dû', es: 'Total adeudado' },
  billing_thank_you_payment: {
    en: 'Thank you for your payment, we really appreciate your business!',
    fr: 'Merci pour votre paiement, nous apprécions vraiment votre confiance !',
    es: '¡Gracias por su pago, realmente agradecemos su preferencia!',
  },
  billing_pro_tip: {
    en: 'PRO TIP: You can enter credit card details into the Billing tab in the portal. Once set up, future invoices will be paid automatically between the 15th and 30th of the month',
    fr: "CONSEIL : Vous pouvez saisir les détails de votre carte de crédit dans l'onglet Facturation du portail. Une fois la carte configurée, les factures futures seront payées automatiquement entre le 15 et le 30 du mois",
    es: 'CONSEJO: Puede introducir los datos de su tarjeta de crédito en la pestaña Facturación del portal. Una vez configurada, las facturas futuras se pagarán automáticamente entre el día 15 y el 30 del mes',
  },
  billing_autopay_set_up: {
    en: 'You are set up for automatic payments. Your card will be charged on or after the 15th of the month.',
    fr: 'Les paiements automatiques sont configurés. Votre carte sera débitée le 15 du mois ou après.',
    es: 'Tiene configurados los pagos automáticos. Se cargará su tarjeta a partir del día 15 del mes.',
  },
  billing_etransfer_caps: { en: 'E-TRANSFER:', fr: 'VIREMENT INTERAC :', es: 'TRANSFERENCIA ELECTRÓNICA:' },
  billing_pay_online: { en: 'PAY ONLINE', fr: 'PAYER EN LIGNE', es: 'PAGAR EN LÍNEA' },
  billing_wire_to: { en: 'WIRE TO', fr: 'VIREMENT À', es: 'TRANSFERIR A' },
  billing_hst: { en: 'HST: {0}', fr: 'TVH : {0}', es: 'HST: {0}' },
  billing_invoice_pdf_title: { en: 'Invoice PDF Display', fr: 'Affichage du PDF de la facture', es: 'Vista del PDF de la factura' },
  billing_invoice_pdf_error: {
    en: 'Could not load PDF for this invoice.',
    fr: 'Impossible de charger le PDF de cette facture.',
    es: 'No se pudo cargar el PDF de esta factura.',
  },

  // ---- GPS devices
  billing_gps_devices_title: { en: 'GPS Devices', fr: 'Appareils GPS', es: 'Dispositivos GPS' },
  billing_gps_page_title: { en: 'Gps Devices - ReadyChek', fr: 'Appareils GPS - ReadyChek', es: 'Dispositivos GPS - ReadyChek' },
  billing_gps_get_more: { en: 'Get More', fr: 'En obtenir plus', es: 'Obtener más' },
  billing_gps_get_more_title: { en: 'Get More GPS Devices', fr: "Obtenir plus d'appareils GPS", es: 'Obtener más dispositivos GPS' },
  billing_gps_get_more_contact: {
    en: 'To request more GPS devices, please contact us at:',
    fr: "Pour demander d'autres appareils GPS, veuillez nous écrire à :",
    es: 'Para solicitar más dispositivos GPS, contáctenos en:',
  },
  billing_gps_get_more_process: {
    en: 'We will process your request and send the devices to you as soon as they are ready.',
    fr: "Nous traiterons votre demande et vous enverrons les appareils dès qu'ils seront prêts.",
    es: 'Procesaremos su solicitud y le enviaremos los dispositivos en cuanto estén listos.',
  },
  billing_gps_search: { en: 'Search IMEI, SIM...', fr: 'Rechercher IMEI, SIM...', es: 'Buscar IMEI, SIM...' },
  billing_gps_device: { en: 'Device', fr: 'Appareil', es: 'Dispositivo' },
  billing_gps_imei: { en: 'IMEI', fr: 'IMEI', es: 'IMEI' },
  billing_gps_sim_number: { en: 'SIM Number', fr: 'Numéro de carte SIM', es: 'Número de SIM' },
  billing_gps_allow_connection: { en: 'Allow Connection', fr: 'Autoriser la connexion', es: 'Permitir conexión' },
  billing_gps_assigned_asset: { en: 'Assigned Asset', fr: 'Actif assigné', es: 'Activo asignado' },
  billing_gps_asset: { en: 'Asset', fr: 'Actif', es: 'Activo' },
  billing_gps_unassigned: { en: 'UNASSIGNED', fr: 'NON ASSIGNÉ', es: 'SIN ASIGNAR' },
  billing_gps_state_status: { en: 'State and Status', fr: 'État et statut', es: 'Estado y situación' },
  billing_gps_removed: { en: 'Removed', fr: 'Retiré', es: 'Eliminado' },
  billing_gps_ignition: { en: 'Ignition', fr: 'Allumage', es: 'Encendido' },
  billing_gps_ignition_on: { en: 'On', fr: 'Allumé', es: 'Encendido' },
  billing_gps_ignition_off: { en: 'Off', fr: 'Éteint', es: 'Apagado' },
  billing_gps_charging: { en: 'Charging', fr: 'En charge', es: 'Cargando' },
  billing_gps_power_reception: { en: 'Power and Reception', fr: 'Alimentation et réception', es: 'Alimentación y recepción' },
  billing_gps_battery: { en: 'Battery', fr: 'Batterie', es: 'Batería' },
  billing_gps_signal: { en: 'Signal', fr: 'Signal', es: 'Señal' },
  billing_gps_satellites: { en: 'Satellites', fr: 'Satellites', es: 'Satélites' },
  billing_gps_last_update: { en: 'Last Update', fr: 'Dernière mise à jour', es: 'Última actualización' },
  billing_gps_last_known_position: { en: 'Last Known Position', fr: 'Dernière position connue', es: 'Última posición conocida' },
  billing_gps_contact_vs_fix: {
    en: 'Last Contact is any traffic from the device, including a heartbeat. Last GPS Fix is the last time it actually located itself.',
    fr: "Le dernier contact correspond à toute communication de l'appareil, y compris un signal de présence. La dernière position GPS est la dernière fois qu'il s'est réellement localisé.",
    es: 'El último contacto es cualquier comunicación del dispositivo, incluida una señal de presencia. La última posición GPS es la última vez que realmente se localizó.',
  },
  billing_gps_last_contact: { en: 'Last Contact', fr: 'Dernier contact', es: 'Último contacto' },
  billing_gps_last_fix: { en: 'Last GPS Fix', fr: 'Dernière position GPS', es: 'Última posición GPS' },
  billing_gps_position: { en: 'Position', fr: 'Position', es: 'Posición' },
  billing_gps_details_title: { en: 'GPS Device Details', fr: "Détails de l'appareil GPS", es: 'Detalles del dispositivo GPS' },
  billing_gps_details_title_imei: { en: 'GPS Device Details: {0}', fr: "Détails de l'appareil GPS : {0}", es: 'Detalles del dispositivo GPS: {0}' },
  billing_gps_details_load_error: {
    en: 'GPS device details could not be loaded.',
    fr: "Impossible de charger les détails de l'appareil GPS.",
    es: 'No se pudieron cargar los detalles del dispositivo GPS.',
  },
  billing_gps_update_title: { en: 'Update GPS Device', fr: "Modifier l'appareil GPS", es: 'Actualizar dispositivo GPS' },
  billing_gps_update_title_imei: { en: 'Update GPS Device: {0}', fr: "Modifier l'appareil GPS : {0}", es: 'Actualizar dispositivo GPS: {0}' },
  billing_gps_load_error: { en: 'GPS device could not be loaded.', fr: "Impossible de charger l'appareil GPS.", es: 'No se pudo cargar el dispositivo GPS.' },
  billing_gps_saving: { en: 'Saving GPS Device...', fr: "Enregistrement de l'appareil GPS...", es: 'Guardando el dispositivo GPS...' },
  billing_gps_selected_company: { en: 'Selected Company', fr: 'Entreprise sélectionnée', es: 'Empresa seleccionada' },
  billing_gps_selected_asset: { en: 'Selected Asset', fr: 'Actif sélectionné', es: 'Activo seleccionado' },
  billing_gps_selected_equipment: { en: 'Selected Equipment', fr: 'Équipement sélectionné', es: 'Equipo seleccionado' },
  billing_gps_selected_vehicle: { en: 'Selected Vehicle', fr: 'Véhicule sélectionné', es: 'Vehículo seleccionado' },
  billing_gps_select_vehicle: { en: 'Please select a vehicle.', fr: 'Veuillez sélectionner un véhicule.', es: 'Seleccione un vehículo.' },
  billing_gps_imei_required: { en: 'IMEI is required.', fr: "L'IMEI est obligatoire.", es: 'El IMEI es obligatorio.' },
  billing_gps_no_update: { en: 'No update received', fr: 'Aucune mise à jour reçue', es: 'No se ha recibido ninguna actualización' },
  billing_gps_no_position: { en: 'No position reported', fr: 'Aucune position signalée', es: 'No se ha informado ninguna posición' },
  billing_gps_no_fix: { en: 'No GPS fix received', fr: 'Aucune position GPS reçue', es: 'No se ha recibido ninguna posición GPS' },
  billing_gps_no_fix_zero: { en: 'No fix (0)', fr: 'Aucune position (0)', es: 'Sin posición (0)' },
  billing_gps_type_obd2: { en: 'OBD2', fr: 'OBD2', es: 'OBD2' },
  billing_gps_type_battery_90: { en: 'Battery 90 Days', fr: 'Batterie 90 jours', es: 'Batería 90 días' },
  billing_gps_type_battery_30: { en: 'Battery 30 Days', fr: 'Batterie 30 jours', es: 'Batería 30 días' },
  billing_gps_type_battery_7: { en: 'Battery 7 Days', fr: 'Batterie 7 jours', es: 'Batería 7 días' },
  billing_gps_type_external_battery: { en: 'External Battery', fr: 'Batterie externe', es: 'Batería externa' },
  billing_gps_signal_none: { en: 'No Signal', fr: 'Aucun signal', es: 'Sin señal' },
  billing_gps_signal_extremely_weak: { en: 'Extremely Weak', fr: 'Extrêmement faible', es: 'Extremadamente débil' },
  billing_gps_signal_very_weak: { en: 'Very Weak', fr: 'Très faible', es: 'Muy débil' },
  billing_gps_signal_good: { en: 'Good', fr: 'Bon', es: 'Buena' },
  billing_gps_signal_excellent: { en: 'Excellent', fr: 'Excellent', es: 'Excelente' },
  billing_gps_battery_none: { en: 'No Power (Shutdown)', fr: 'Aucune alimentation (arrêt)', es: 'Sin energía (apagado)' },
  billing_gps_battery_extremely_low: { en: 'Extremely Low', fr: 'Extrêmement faible', es: 'Extremadamente baja' },
  billing_gps_battery_very_low: { en: 'Very Low', fr: 'Très faible', es: 'Muy baja' },
  billing_gps_battery_low: { en: 'Low', fr: 'Faible', es: 'Baja' },
  billing_gps_battery_medium: { en: 'Medium', fr: 'Moyenne', es: 'Media' },
  billing_gps_battery_high: { en: 'High', fr: 'Élevée', es: 'Alta' },
  billing_gps_battery_full: { en: 'Full', fr: 'Pleine', es: 'Llena' },

  // ---- Map
  billing_map_page_title: { en: 'Map - ReadyChek', fr: 'Carte - ReadyChek', es: 'Mapa - ReadyChek' },
  billing_map_loading_route: { en: 'Loading Route Data...', fr: "Chargement des données d'itinéraire...", es: 'Cargando datos de la ruta...' },
  billing_map_hide_card: { en: 'Hide Card', fr: 'Masquer la fiche', es: 'Ocultar tarjeta' },
  billing_map_show_card: { en: 'Show Card', fr: 'Afficher la fiche', es: 'Mostrar tarjeta' },
  billing_map_hide: { en: 'Hide', fr: 'Masquer', es: 'Ocultar' },
  billing_map_gps: { en: 'GPS', fr: 'GPS', es: 'GPS' },
  billing_map_locations: { en: 'Locations', fr: 'Lieux', es: 'Ubicaciones' },
  billing_map_search_items: { en: 'Search items...', fr: 'Rechercher...', es: 'Buscar elementos...' },
  billing_map_search: { en: 'Search...', fr: 'Rechercher...', es: 'Buscar...' },
  billing_map_gps_assets_count: { en: 'Gps Assets ({0} of {1})', fr: 'Actifs GPS ({0} sur {1})', es: 'Activos GPS ({0} de {1})' },
  billing_map_no_gps_assets: { en: 'No Gps Assets Found', fr: 'Aucun actif GPS trouvé', es: 'No se encontraron activos GPS' },
  billing_map_inspections_count: { en: 'Inspections ({0} of {1})', fr: 'Inspections ({0} sur {1})', es: 'Inspecciones ({0} de {1})' },
  billing_map_no_inspections_today: { en: 'No inspections today', fr: "Aucune inspection aujourd'hui", es: 'No hay inspecciones hoy' },
  billing_map_no_inspections_day: { en: 'No inspections on this day', fr: 'Aucune inspection ce jour-là', es: 'No hay inspecciones este día' },
  billing_map_locations_count: { en: 'Locations ({0})', fr: 'Lieux ({0})', es: 'Ubicaciones ({0})' },
  billing_map_all_inspections_visible: {
    en: 'All Inspections are visible',
    fr: 'Toutes les inspections sont affichées',
    es: 'Todas las inspecciones están visibles',
  },
  billing_map_all_gps_visible: {
    en: 'All GPS devices are visible',
    fr: 'Tous les appareils GPS sont affichés',
    es: 'Todos los dispositivos GPS están visibles',
  },
  billing_map_location_deleted: { en: 'Location deleted successfully.', fr: 'Lieu supprimé avec succès.', es: 'Ubicación eliminada correctamente.' },
  billing_map_device_unknown: {
    en: 'Unable to determine device to load, please refresh the page.',
    fr: "Impossible de déterminer l'appareil à charger, veuillez actualiser la page.",
    es: 'No se puede determinar el dispositivo que cargar; actualice la página.',
  },
  billing_map_legend: { en: 'Legend', fr: 'Légende', es: 'Leyenda' },
  billing_map_legend_moving: { en: 'Moving, 10 to 120 km/h', fr: 'En mouvement, de 10 à 120 km/h', es: 'En movimiento, de 10 a 120 km/h' },
  billing_map_legend_slow: { en: 'Moving under 10 km/h', fr: 'En mouvement à moins de 10 km/h', es: 'En movimiento a menos de 10 km/h' },
  billing_map_legend_fast: { en: 'Moving 120 to 140 km/h', fr: 'En mouvement de 120 à 140 km/h', es: 'En movimiento de 120 a 140 km/h' },
  billing_map_legend_very_fast: { en: 'Moving over 140 km/h', fr: 'En mouvement à plus de 140 km/h', es: 'En movimiento a más de 140 km/h' },
  billing_map_legend_stopped: { en: 'Stopped, engine off', fr: 'Arrêté, moteur coupé', es: 'Detenido, motor apagado' },
  billing_map_legend_idle: { en: 'Idling 3 to 10 minutes', fr: 'Ralenti de 3 à 10 minutes', es: 'Ralentí de 3 a 10 minutos' },
  billing_map_legend_long_idle: { en: 'Idling 10 minutes or more', fr: 'Ralenti de 10 minutes ou plus', es: 'Ralentí de 10 minutos o más' },
  billing_map_go_to_start: { en: 'Go to Start', fr: 'Aller au début', es: 'Ir al inicio' },
  billing_map_playback_speed: { en: 'Playback speed', fr: 'Vitesse de lecture', es: 'Velocidad de reproducción' },
  billing_map_route_failed: {
    en: 'The route could not be loaded. Try again in a moment.',
    fr: "Le trajet n'a pas pu être chargé. Réessayez dans un instant.",
    es: 'No se pudo cargar la ruta. Inténtelo de nuevo en un momento.',
  },
  billing_map_no_route: {
    en: 'No route found for this asset on the selected day',
    fr: 'Aucun itinéraire trouvé pour cet actif à la date sélectionnée',
    es: 'No se encontró ninguna ruta para este activo en el día seleccionado',
  },
  billing_duration_h_m: { en: '{0}h {1}m', fr: '{0} h {1} min', es: '{0} h {1} min' },
  billing_duration_m: { en: '{0}m', fr: '{0} min', es: '{0} min' },
  billing_map_stop: { en: 'Stop', fr: 'Arrêt', es: 'Parada' },
  billing_map_idle: { en: 'Idle', fr: 'Ralenti', es: 'Ralentí' },
  billing_map_moving: { en: 'Moving', fr: 'En mouvement', es: 'En movimiento' },
  billing_map_moving_slow: { en: 'Moving under 10 km/h', fr: 'En mouvement à moins de 10 km/h', es: 'En movimiento a menos de 10 km/h' },
  billing_map_moving_fast: { en: 'Moving 120 to 140 km/h', fr: 'En mouvement de 120 à 140 km/h', es: 'En movimiento de 120 a 140 km/h' },
  billing_map_moving_very_fast: { en: 'Moving over 140 km/h', fr: 'En mouvement à plus de 140 km/h', es: 'En movimiento a más de 140 km/h' },
  billing_map_event_duration: { en: '{0}: {1}', fr: '{0} : {1}', es: '{0}: {1}' },
  billing_map_stop_letter: { en: 'S', fr: 'A', es: 'P' },
  billing_map_idle_letter: { en: 'I', fr: 'R', es: 'R' },
  billing_map_cvor_title: { en: 'CVOR: {0}', fr: 'CVOR : {0}', es: 'CVOR: {0}' },
  billing_map_hq_title: { en: 'HQ: {0}', fr: 'Siège : {0}', es: 'Sede: {0}' },
  billing_map_hq_label: { en: 'HQ', fr: 'Siège', es: 'Sede' },
  billing_map_place_of_interest: { en: 'Place of Interest', fr: "Point d'intérêt", es: 'Punto de interés' },
  billing_map_location_details: { en: 'Location Details', fr: 'Détails du lieu', es: 'Detalles de la ubicación' },
  billing_map_vehicle_details: { en: 'Vehicle Details', fr: 'Détails du véhicule', es: 'Detalles del vehículo' },
  billing_map_pretrip_inspection: { en: 'Pre-Trip Inspection', fr: 'Inspection avant le départ', es: 'Inspección previa al viaje' },
  billing_map_status_label: { en: 'Status:', fr: 'Statut :', es: 'Estado:' },
  billing_map_driver_label: { en: 'Driver:', fr: 'Conducteur :', es: 'Conductor:' },
  billing_map_date_label: { en: 'Date:', fr: 'Date :', es: 'Fecha:' },
  billing_map_schedule_label: { en: 'Schedule:', fr: 'Annexe :', es: 'Programa:' },
  billing_map_vehicle_label: { en: 'Vehicle:', fr: 'Véhicule :', es: 'Vehículo:' },
  billing_map_vehicle_value: { en: 'Vehicle: {0}', fr: 'Véhicule : {0}', es: 'Vehículo: {0}' },
  billing_map_location_label: { en: 'Location:', fr: 'Lieu :', es: 'Ubicación:' },
  billing_map_speed_label: { en: 'Speed:', fr: 'Vitesse :', es: 'Velocidad:' },
  billing_map_updated_label: { en: 'Updated:', fr: 'Mis à jour :', es: 'Actualizado:' },
  billing_map_no_current_inspection: { en: 'No current inspection', fr: 'Aucune inspection en cours', es: 'No hay inspección actual' },
  billing_map_live_telematics: { en: 'Live Telematics', fr: 'Télématique en direct', es: 'Telemática en vivo' },
  billing_map_last_update_label: { en: 'Last Update:', fr: 'Dernière mise à jour :', es: 'Última actualización:' },
  billing_map_offline_tooltip: {
    en: 'Device may be offline or inactive (no update in over 10 hours)',
    fr: "L'appareil est peut-être hors ligne ou inactif (aucune mise à jour depuis plus de 10 heures)",
    es: 'Es posible que el dispositivo esté desconectado o inactivo (sin actualizaciones en más de 10 horas)',
  },
  billing_map_last_fix_label: { en: 'Last GPS Fix:', fr: 'Dernière position GPS :', es: 'Última posición GPS:' },
  billing_map_stale_fix_tooltip: {
    en: 'This device is still checking in, but it has stopped reporting its position. That usually means the GPS antenna is disconnected or obstructed rather than a problem with the service.',
    fr: "Cet appareil communique toujours, mais il a cessé de signaler sa position. Cela signifie généralement que l'antenne GPS est débranchée ou obstruée, et non qu'il y a un problème avec le service.",
    es: 'Este dispositivo sigue comunicándose, pero ha dejado de informar su posición. Normalmente significa que la antena GPS está desconectada u obstruida, no que haya un problema con el servicio.',
  },
  billing_map_location_time_label: { en: 'Location Time:', fr: 'Heure de la position :', es: 'Hora de la posición:' },
  billing_map_chip_ignition: { en: 'Ignition: {0}', fr: 'Allumage : {0}', es: 'Encendido: {0}' },
  billing_map_chip_power: { en: 'Power: {0}', fr: 'Alimentation : {0}', es: 'Alimentación: {0}' },
  billing_map_chip_battery: { en: 'Battery: {0}', fr: 'Batterie : {0}', es: 'Batería: {0}' },
  billing_map_chip_gsm: { en: 'GSM: {0}', fr: 'GSM : {0}', es: 'GSM: {0}' },
  billing_map_chip_satellites: { en: 'Satellites: {0}', fr: 'Satellites : {0}', es: 'Satélites: {0}' },
  billing_map_sat_no_position: {
    en: 'This device has not reported a position yet',
    fr: "Cet appareil n'a pas encore signalé de position",
    es: 'Este dispositivo aún no ha informado ninguna posición',
  },
  billing_map_sat_none: {
    en: 'The tracker is online but cannot see any satellites - check the GPS antenna is connected and has a clear view of the sky',
    fr: "Le traceur est en ligne, mais ne capte aucun satellite - vérifiez que l'antenne GPS est branchée et a une vue dégagée du ciel",
    es: 'El localizador está en línea, pero no detecta ningún satélite: compruebe que la antena GPS esté conectada y tenga una vista despejada del cielo',
  },
  billing_map_sat_used: {
    en: 'Satellites used for the last position report. 4 or more (green) is normal. Fewer than 4 (amber) means the position may be less accurate.',
    fr: 'Satellites utilisés pour le dernier signalement de position. 4 ou plus (vert) : normal. Moins de 4 (ambre) : la position peut être moins précise.',
    es: 'Satélites usados para el último informe de posición. 4 o más (verde) es normal. Menos de 4 (ámbar): la posición puede ser menos precisa.',
  },
  billing_map_ago: { en: '{0} ago', fr: 'il y a {0}', es: 'hace {0}' },
  billing_map_160_circle: { en: '160 Circle', fr: 'Cercle de 160', es: 'Círculo de 160' },
  billing_map_160km: { en: '160km', fr: '160 km', es: '160 km' },
  billing_map_route: { en: 'Route', fr: 'Itinéraire', es: 'Ruta' },
  billing_map_no_gps_fix: { en: 'No GPS fix', fr: 'Aucune position GPS', es: 'Sin posición GPS' },
  billing_map_no_gps_fix_hint: {
    en: 'Online but not reporting a position - check the GPS antenna',
    fr: "En ligne, mais ne signale aucune position - vérifiez l'antenne GPS",
    es: 'En línea, pero sin informar posición: compruebe la antena GPS',
  },
  // Chip tooltips on the Map's GPS list and vehicle card: what the chip means, then when to act.
  billing_map_tip_speed: {
    en: 'Last recorded speed. Green is 10 to 120 km/h. Amber is over 120 km/h - worth a word with the driver. Red is over 140 km/h.',
    fr: 'Dernière vitesse enregistrée. Vert : de 10 à 120 km/h. Ambre : plus de 120 km/h - à signaler au conducteur. Rouge : plus de 140 km/h.',
    es: 'Última velocidad registrada. Verde: de 10 a 120 km/h. Ámbar: más de 120 km/h; conviene hablar con el conductor. Rojo: más de 140 km/h.',
  },
  billing_map_tip_inspection: {
    en: 'Result of the latest inspection in the last 24 hours. Minor (amber): book the repair. Major (red): keep the vehicle off the road until it is repaired.',
    fr: "Résultat de la dernière inspection des 24 dernières heures. Mineur (ambre) : prévoir la réparation. Majeur (rouge) : garder le véhicule hors de la route jusqu'à sa réparation.",
    es: 'Resultado de la última inspección de las últimas 24 horas. Menor (ámbar): programe la reparación. Mayor (rojo): no use el vehículo hasta repararlo.',
  },
  billing_map_tip_battery: {
    en: "The tracker's own backup battery. Low or worse (amber) means it is running without vehicle power. No Power (red) means it has shut down and the vehicle cannot be tracked.",
    fr: "Batterie de secours du traceur. Faible ou moins (ambre) : il fonctionne sans l'alimentation du véhicule. Aucune alimentation (rouge) : il s'est arrêté et le véhicule ne peut plus être suivi.",
    es: 'Batería de respaldo del rastreador. Baja o menos (ámbar): funciona sin la energía del vehículo. Sin energía (rojo): se ha apagado y el vehículo no se puede rastrear.',
  },
  billing_map_tip_last_contact: {
    en: 'The tracker has not reported its position for 20 hours or more. Amber up to 36 hours, red after that. Red on a vehicle that should be working points at a tracker power or signal problem.',
    fr: "Le traceur n'a pas signalé sa position depuis 20 heures ou plus. Ambre jusqu'à 36 heures, rouge ensuite. Du rouge sur un véhicule en service indique un problème d'alimentation ou de signal du traceur.",
    es: 'El rastreador no ha informado su posición en 20 horas o más. Ámbar hasta 36 horas, rojo después. Rojo en un vehículo que debería estar trabajando indica un problema de energía o señal del rastreador.',
  },
  billing_map_tip_ignition: {
    en: "Whether the engine is on, as the tracker last saw it. Information only.",
    fr: "Indique si le moteur est en marche, selon la dernière lecture du traceur. À titre indicatif.",
    es: 'Indica si el motor está encendido, según la última lectura del rastreador. Solo informativo.',
  },
  billing_map_tip_power: {
    en: "Whether the tracker is getting power from the vehicle. No while the vehicle is working means the tracker is on its backup battery - check its wiring.",
    fr: "Indique si le traceur est alimenté par le véhicule. Non pendant que le véhicule travaille : le traceur est sur sa batterie de secours - vérifiez son branchement.",
    es: 'Indica si el rastreador recibe energía del vehículo. No mientras el vehículo trabaja: el rastreador usa su batería de respaldo; revise el cableado.',
  },
  billing_map_tip_gsm: {
    en: 'Cellular signal the tracker uses to send its reports. Very Weak or worse (red) delays or stops updates until the vehicle reaches better coverage.',
    fr: "Signal cellulaire utilisé par le traceur pour envoyer ses rapports. Très faible ou moins (rouge) retarde ou bloque les mises à jour jusqu'à une meilleure couverture.",
    es: 'Señal celular que el rastreador usa para enviar sus informes. Muy débil o menos (rojo) retrasa o detiene las actualizaciones hasta tener mejor cobertura.',
  },
  billing_map_moving_without_pretrip_one: {
    en: '1 vehicle moving without a valid pre-trip',
    fr: '1 véhicule roule sans pré-inspection valide',
    es: '1 vehículo circula sin inspección previa válida',
  },
  billing_map_moving_without_pretrip_other: {
    en: '{0} vehicles moving without a valid pre-trip',
    fr: '{0} véhicules roulent sans pré-inspection valide',
    es: '{0} vehículos circulan sin inspección previa válida',
  },
  billing_map_moving_without_pretrip_risk: {
    en: 'The driver risks a roadside fine and CVOR points. Contact them now to stop and complete a pre-trip.',
    fr: "Le conducteur risque une amende sur la route et des points au CVOR. Contactez-le maintenant pour qu'il s'arrête et fasse une pré-inspection.",
    es: 'El conductor se arriesga a una multa en carretera y a puntos en el CVOR. Contáctelo ahora para que se detenga y complete una inspección previa.',
  },
  billing_map_outside_160: { en: 'Outside 160!', fr: 'Hors 160 km!', es: '¡Fuera de 160 km!' },
  billing_map_outside_160_hint: {
    en: 'More than 160 km from where its pre-trip was done. Beyond that radius the driver needs a full daily log - make sure they are keeping one.',
    fr: "À plus de 160 km du lieu de sa pré-inspection. Au-delà de ce rayon, le conducteur doit tenir un journal de bord complet - assurez-vous qu'il le fait.",
    es: 'A más de 160 km de donde se hizo su inspección previa. Más allá de ese radio el conductor necesita un registro diario completo; asegúrese de que lo lleve.',
  },
  billing_map_outside_160_count_one: {
    en: '1 vehicle outside its 160 km radius',
    fr: '1 véhicule hors de son rayon de 160 km',
    es: '1 vehículo fuera de su radio de 160 km',
  },
  billing_map_outside_160_count_other: {
    en: '{0} vehicles outside their 160 km radius',
    fr: '{0} véhicules hors de leur rayon de 160 km',
    es: '{0} vehículos fuera de su radio de 160 km',
  },
  billing_map_outside_160_risk: {
    en: 'Beyond 160 km the driver needs a full daily log. Without one they risk a roadside fine and CVOR points.',
    fr: 'Au-delà de 160 km, le conducteur doit tenir un journal de bord complet. Sans cela, il risque une amende sur la route et des points au CVOR.',
    es: 'Más allá de 160 km el conductor necesita un registro diario completo. Sin él se arriesga a una multa en carretera y a puntos en el CVOR.',
  },
  billing_map_no_valid_pretrip: { en: 'No valid pre-trip', fr: 'Aucune pré-inspection valide', es: 'Sin inspección previa válida' },
  billing_map_no_valid_pretrip_hint: {
    en: 'Commercial vehicle with a schedule, moving at over 30 km/h, with no pre-trip inspection in the last 24 hours. Contact the driver: a pre-trip is required before the vehicle is driven.',
    fr: "Véhicule commercial avec un calendrier, roulant à plus de 30 km/h, sans pré-inspection dans les dernières 24 heures. Contactez le conducteur : une pré-inspection est requise avant de conduire le véhicule.",
    es: 'Vehículo comercial con calendario, circulando a más de 30 km/h, sin inspección previa en las últimas 24 horas. Contacte al conductor: se requiere una inspección previa antes de conducirlo.',
  },

  // ---- Place of interest dialogs
  billing_location_edit_title: { en: 'Edit Place of Interest', fr: "Modifier le point d'intérêt", es: 'Editar punto de interés' },
  billing_location_save_title: { en: 'Save Place of Interest', fr: "Enregistrer le point d'intérêt", es: 'Guardar punto de interés' },
  billing_location_intro: {
    en: 'Enter the details for this place of interest. This point of interest will be used to alert you when a vehicle arrives or leaves.',
    fr: "Saisissez les détails de ce point d'intérêt. Il servira à vous avertir lorsqu'un véhicule arrive ou repart.",
    es: 'Introduzca los datos de este punto de interés. Se utilizará para avisarle cuando un vehículo llegue o salga.',
  },
  billing_location_gps_coordinate: { en: 'GPS Coordinate', fr: 'Coordonnées GPS', es: 'Coordenadas GPS' },
  billing_location_street: { en: 'No. and Street', fr: 'N° et rue', es: 'Número y calle' },
  billing_location_calculated: { en: 'Will be calculated on save', fr: "Sera calculé à l'enregistrement", es: 'Se calculará al guardar' },
  billing_location_name_required: {
    en: 'Please fill in a value for Name.',
    fr: 'Veuillez saisir une valeur pour le nom.',
    es: 'Introduzca un valor para el nombre.',
  },
  billing_location_no_coordinates: {
    en: 'Could not find coordinates for this address.',
    fr: 'Impossible de trouver les coordonnées de cette adresse.',
    es: 'No se encontraron coordenadas para esta dirección.',
  },
  billing_location_save_failed: { en: 'Failed to save location.', fr: "Échec de l'enregistrement du lieu.", es: 'No se pudo guardar la ubicación.' },
  billing_location_delete_title: { en: 'Delete Place of Interest', fr: "Supprimer le point d'intérêt", es: 'Eliminar punto de interés' },
  billing_location_delete_confirm: {
    en: 'Are you sure you want to delete this location? This action cannot be undone.',
    fr: 'Voulez-vous vraiment supprimer ce lieu ? Cette action est irréversible.',
    es: '¿Está seguro de que desea eliminar esta ubicación? Esta acción no se puede deshacer.',
  },
  billing_location_delete_failed: { en: 'Failed to delete location.', fr: 'Échec de la suppression du lieu.', es: 'No se pudo eliminar la ubicación.' },
};
